import cx from "classnames"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { useState, useEffect } from "react"
import { P2 } from "components/Typography"
import useScript from "effects/useScript"
import {
  trackEvent,
  trackSegmentFormOpened,
  trackSegmentFormSubmission,
} from "lib/googleTagManager"
import * as css from "./HubspotFormEmbed.module.css"

function HubspotFormEmbed({
  formId,
  onSubmit,
  trackSubmit,
  thankYouMessage,
  className,
  wrapperCssName,
  embedOptions = {},
  hiddenFields = {},
  width,
  variant,
  submitText,
}) {
  const [submitted, setSubmitted] = useState(false)
  const [rendered, setRendered] = useState(false)
  const hbsptLoaded = useScript("https://js.hsforms.net/forms/v2.js")
  const id = `hubspot-form-${formId}-${Date.now()}`

  useEffect(() => {
    let hasSubmitted = false

    const handleSubmit = () => {
      // Prevent duplicate calls
      if (hasSubmitted) return
      hasSubmitted = true

      trackEvent("formSubmission", {
        formSubmissionEventName: trackSubmit || formId,
      })
      trackSegmentFormSubmission({
        form_id: formId,
        form_name: trackSubmit || formId,
        form_type: "hubspot_embed",
      })

      if (onSubmit) {
        onSubmit()
        setSubmitted(true)
      }
    }

    if (typeof window === "object" && hbsptLoaded && rendered) {
      // Polyfill jQuery for HubSpot
      const jQuery = ($) => {
        if (typeof nodeOrSelector === "string") {
          return document.querySelector($)
        }
        return $
      }

      window.jQuery = window.jQuery || jQuery

      const onFormReady = ($form) => {
        trackSegmentFormOpened({
          form_id: formId,
          form_name: trackSubmit || formId,
          form_type: "hubspot_embed",
        })
        // Pre-fill hidden fields. We default `source_page` to the current
        // pathname unless the caller has explicitly overridden it. This
        // keeps the HubSpot `source_page` CRM property meaningful (e.g.
        // "/contact-us-about-agor/") instead of whatever HubSpot's
        // auto-detection picks up (which has been observed to land on "/"
        // in some embed contexts). Forms without a `source_page` field
        // defined silently no-op — querySelector returns null.
        const fieldsToFill = {
          source_page:
            typeof window !== "undefined" ? window.location.pathname : "",
          ...hiddenFields,
        }
        Object.entries(fieldsToFill).forEach(([fieldName, value]) => {
          const input = $form?.find?.(`[name="${fieldName}"]`)
          if (input?.val) {
            input.val(value)
          } else {
            // Fallback for non-jQuery form reference
            const formEl = document.querySelector(`#${id}`)
            const inputEl = formEl?.querySelector(`[name="${fieldName}"]`)
            if (inputEl) {
              inputEl.value = value
            }
          }
        })
        // Override HubSpot's configured submit button text
        if (submitText) {
          const formEl = document.querySelector(`#${id}`)
          const btn = formEl?.querySelector('[type="submit"]')
          if (btn) {
            if (btn.tagName === "INPUT") {
              btn.value = submitText
            } else {
              btn.textContent = submitText
            }
          }
        }
        // Call any custom onFormReady from embedOptions
        if (embedOptions.onFormReady) {
          embedOptions.onFormReady($form)
        }
      }

      window.hbspt.forms.create({
        portalId: "5901754",
        formId,
        target: `#${id}`,
        css: "", // remove HubSpot's CSS
        cssRequired: "", // remove HubSpot's CSS
        cssClass: className || "",
        errorMessageClass: "",
        onFormReady,
        // Only react to onFormSubmitted (post-POST). onFormSubmit fires
        // BEFORE the request is sent; if a caller's onSubmit handler
        // unmounts this component (e.g. swaps the form for a success
        // message), the in-flight POST is killed and the submission
        // never reaches HubSpot. See:
        // https://developers.hubspot.com/docs/cms/building-blocks/forms#embedded-form-callbacks
        onFormSubmitted: () => {
          handleSubmit()
        },
        ...embedOptions,
      })
    }
  }, [
    className,
    embedOptions,
    hiddenFields,
    hbsptLoaded,
    rendered,
    formId,
    id,
    onSubmit,
    trackSubmit,
  ])

  if (!hbsptLoaded) {
    return null
  }

  if (thankYouMessage && submitted) return <P2>{thankYouMessage}</P2>

  const classNames = cx(
    css.root,
    {
      [css.centered]: !!width,
      [css.partnerCta]: variant === "partner-cta",
    },
    wrapperCssName
  )

  return (
    <div
      id={id}
      ref={() => !rendered && setRendered(true)}
      className={classNames}
      style={width && { width }}
    />
  )
}

HubspotFormEmbed.propTypes = {
  formId: PropTypes.string.isRequired,
  trackSubmit: PropTypes.string,
  thankYouMessage: PropTypes.string,
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  wrapperCssName: PropTypes.string,
  embedOptions: PropTypes.shape({}),
  /** Key-value pairs for pre-filling hidden form fields */
  hiddenFields: PropTypes.objectOf(PropTypes.string),
  width: PropTypes.number,
  /** Layout variant - "partner-cta" for compact two-column layout */
  variant: PropTypes.oneOf(["partner-cta"]),
  /** Overrides the submit button text configured in HubSpot */
  submitText: PropTypes.string,
}

export default HubspotFormEmbed

export const query = graphql`
  fragment HubspotForm on ContentfulHubspotForm {
    id
    __typename
    formId
    trackSubmit: formSubmitTracking
    thankYouMessage
    width
  }
`
